
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Satyaki Bose
 * CustomToast
 * Reusable Custom Toast component
 */


import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography,
  IconButton,
  Slide,
  Snackbar
} from '@material-ui/core';
import Icon from '../icons/Icon';
import mixins from '../../assets/styles/mixins.scss';

const useStyles = makeStyles(() => ({
  snackbarContent: {
    minWidth: 396,
    maxWidth: 396,
    borderRadius: 8,
    backgroundColor: '#F6F6FB',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'stretch',
    border: '1px solid #e0e0e0',
    height: 118,
    padding: 2,
    boxShadow: '0px 4px 20px rgba(13, 0, 77, 0.05), 0px 4px 20px rgba(13, 0, 77, 0.10), 0px 4px 20px rgba(13, 0, 77, 0.15)',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      width: 4,
      height: '100%',
      borderRadius: '2px 0 0 2px',
      backgroundColor: 'var(--sidebar-color)',
      zIndex: 1
    }
  },
  contentContainer: {
    flex: 1,
    padding: '8px 16px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    position: 'relative',
    zIndex: 2,
    justifyContent: 'center'
  },
  closeButton: {
    position: 'absolute',
    top: 6,
    right: 12,
    padding: 4,
    color: mixins.navyColor
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  statusIcon: {
    fontSize: 20,
    marginRight: 12
  },
  titleText: {
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight600,
    fontFamily: mixins.fontFamilyPlusSans,
    color: mixins.navyColor,
    margin: 0,
    lineHeight: mixins.lineHeight24
  },
  messageText: {
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    fontFamily: mixins.fontFamilyPlusSans,
    lineHeight: mixins.lineHeight24,
    color: mixins.navyColor,
    margin: 0
  },
  actionText: {
    fontSize: mixins.fontSize16,
    lineHeight: mixins.lineHeight24,
    margin: 0,
    cursor: 'pointer',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  }
}));

const CustomToast = ({
  open = false,
  onClose,
  title = '',
  message = '',
  actionText = '',
  onActionClick,
  variant,
  customColors,
  autoHideDuration,
  position,
  showCloseButton,
  ...props
}) => {
  const classes = useStyles();

  const getIcon = () => {
    const iconProps = {
      className: classes.statusIcon,
      style: { color: customColors.accentColor1 }
    };

    switch (variant) {
      case 'info':
        return <Icon name="FeedbackInfoIcon" {...iconProps} />;
      case 'error':
        return <Icon name="FeedbackFailureIcon" {...iconProps} />;
      case 'custom':
        return <Icon name="ToastIconTick" {...iconProps} />;
      default:
        return <Icon name="ToastIconTick" {...iconProps} />;
    }
  };
  const getAnchorOrigin = () => {
    const positions = {
      'top-right': { vertical: 'top', horizontal: 'right' },
      'top-left': { vertical: 'top', horizontal: 'left' },
      'bottom-right': { vertical: 'bottom', horizontal: 'right' },
      'bottom-left': { vertical: 'bottom', horizontal: 'left' }
    };

    return positions[position] || positions['top-right'];
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={onClose}
      anchorOrigin={getAnchorOrigin()}
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'left' }}
      transitionDuration={{ enter: 500, exit: 450 }}
      {...props}
    >
      <div
        className={classes.snackbarContent}
        style={{ '--sidebar-color': customColors.accentColor2 }}
      >
        <div className={classes.contentContainer}>
          {showCloseButton && (
            <IconButton
              className={classes.closeButton}
              onClick={onClose}
              size="small"
            >
              <Icon name="CloseIcon" />
            </IconButton>
          )}

          <div className={classes.iconContainer}>
            {getIcon()}
            {title && (
              <Typography className={classes.titleText}>
                {title}
              </Typography>
            )}
          </div>

          {message && (
            <Typography className={classes.messageText}>
              {message}
            </Typography>
          )}

          {actionText && (
            <Typography
              className={classes.actionText}
              onClick={onActionClick}
              style={{ color: customColors.accentColor1 }}
            >
              {actionText}
            </Typography>
          )}
        </div>
      </div>
    </Snackbar>
  );
};

CustomToast.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  message: PropTypes.string,
  actionText: PropTypes.string,
  onActionClick: PropTypes.func,
  variant: PropTypes.string,
  customColors: PropTypes.object,
  autoHideDuration: PropTypes.number,
  position: PropTypes.string,
  showCloseButton: PropTypes.bool
};

CustomToast.defaultProps = {
  open: false,
  onClose: () => {},
  title: '',
  message: '',
  actionText: '',
  onActionClick: () => {},
  variant: 'success',
  customColors: {
  },
  autoHideDuration: 5000,
  position: 'top-right',
  showCloseButton: true
};

export default CustomToast;
